// src/lib/bunnyStorage.js
// Bunny.net Storage service for image management
// Ultra-cheap: $0.01/GB storage, $0.01/GB bandwidth, no transformation limits

const BUNNY_STORAGE_ZONE = import.meta.env.VITE_BUNNY_STORAGE_ZONE;
const BUNNY_API_KEY = import.meta.env.VITE_BUNNY_API_KEY;
const BUNNY_REGION = import.meta.env.VITE_BUNNY_REGION || 'sg'; // Default to Singapore

// Usage tracking for Bunny.net Storage
let bandwidthUsed = 0;
let storageUsed = 0;

/**
 * Track bandwidth usage
 * @param {number} bytes - Bytes transferred
 */
export const trackBandwidth = (bytes) => {
  bandwidthUsed += bytes;
  
  // Log usage every 100MB
  if (bandwidthUsed % (100 * 1024 * 1024) === 0) {
    const cost = (bandwidthUsed / 1024 / 1024 / 1024) * 0.01; // $0.01/GB
    console.log(`📊 Bunny.net Bandwidth: ${Math.round(bandwidthUsed / 1024 / 1024)}MB ($${cost.toFixed(4)})`);
  }
  
  // Store in localStorage for persistence
  localStorage.setItem('bunny_bandwidth', bandwidthUsed.toString());
};

/**
 * Track storage usage
 * @param {number} bytes - Bytes stored
 */
export const trackStorage = (bytes) => {
  storageUsed += bytes;
  
  // Log usage every 1GB
  if (storageUsed % (1024 * 1024 * 1024) === 0) {
    const cost = (storageUsed / 1024 / 1024 / 1024) * 0.01; // $0.01/GB
    console.log(`📊 Bunny.net Storage: ${Math.round(storageUsed / 1024 / 1024 / 1024)}GB ($${cost.toFixed(4)})`);
  }
  
  // Store in localStorage for persistence
  localStorage.setItem('bunny_storage', storageUsed.toString());
};

/**
 * Get current usage statistics
 * @returns {object} Usage stats
 */
export const getUsageStats = () => {
  const storedBandwidth = localStorage.getItem('bunny_bandwidth');
  const storedStorage = localStorage.getItem('bunny_storage');
  
  const currentBandwidth = storedBandwidth ? parseInt(storedBandwidth) : 0;
  const currentStorage = storedStorage ? parseInt(storedStorage) : 0;
  
  const bandwidthCost = (currentBandwidth / 1024 / 1024 / 1024) * 0.01;
  const storageCost = (currentStorage / 1024 / 1024 / 1024) * 0.01;
  
  return {
    bandwidth: {
      used: currentBandwidth,
      cost: bandwidthCost,
      costFormatted: `$${bandwidthCost.toFixed(4)}`
    },
    storage: {
      used: currentStorage,
      cost: storageCost,
      costFormatted: `$${storageCost.toFixed(4)}`
    },
    total: {
      cost: bandwidthCost + storageCost,
      costFormatted: `$${(bandwidthCost + storageCost).toFixed(4)}`
    }
  };
};

/**
 * Reset usage counter (call monthly)
 */
export const resetUsageCounter = () => {
  bandwidthUsed = 0;
  storageUsed = 0;
  localStorage.removeItem('bunny_bandwidth');
  localStorage.removeItem('bunny_storage');
  console.log('🔄 Bunny.net usage counter reset');
};

/**
 * Get public URL for an image
 * @param {string} path - Storage path
 * @returns {string} Public URL
 */
export const getImageUrl = (path) => {
  if (!path || !BUNNY_STORAGE_ZONE) {
    console.warn('Missing path or storage zone for image');
    return '';
  }
  
  // Use Pull Zone CDN URL with amlo-app folder prefix
  return `https://amlo-app-cdn.b-cdn.net/amlo-app/${path}`;
};

/**
 * Get optimized image URL with client-side transformations
 * @param {string} path - Storage path
 * @param {object} options - Transformation options
 * @returns {string} Optimized URL
 */
export const getOptimizedImageUrl = (path, options = {}) => {
  const baseUrl = getImageUrl(path);
  
  // For Bunny.net, we'll use client-side optimization
  // You can add query parameters for basic transformations
  const params = new URLSearchParams();
  
  if (options.width) params.append('width', options.width);
  if (options.height) params.append('height', options.height);
  if (options.quality) params.append('quality', options.quality);
  
  return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
};

/**
 * Get thumbnail URL (same as base URL for now)
 * @param {string} path - Storage path
 * @param {number} size - Thumbnail size
 * @returns {string} Thumbnail URL
 */
export const getThumbnailUrl = (path, size = 300) => {
  return getOptimizedImageUrl(path, { width: size });
};

/**
 * Get full-size image URL
 * @param {string} path - Storage path
 * @returns {string} Full-size URL
 */
export const getFullSizeUrl = (path) => {
  return getImageUrl(path);
};

/**
 * Get raw image URL (no transformations)
 * @param {string} path - Storage path
 * @returns {string} Raw URL
 */
export const getRawImageUrl = (path) => {
  return getImageUrl(path);
};

/**
 * Smart image URL selector
 * @param {string} path - Storage path
 * @param {string} context - 'thumbnail', 'full', or 'raw'
 * @returns {string} Appropriate URL
 */
export const getSmartImageUrl = (path, context = 'thumbnail') => {
  switch (context) {
    case 'raw':
      return getRawImageUrl(path);
    case 'full':
      return getFullSizeUrl(path);
    case 'thumbnail':
    default:
      return getThumbnailUrl(path);
  }
};

/**
 * Upload image to Bunny.net Storage
 * @param {File} file - Image file to upload
 * @param {string} path - Storage path (e.g., 'amlo-app/category/image.jpg')
 * @param {object} options - Upload options
 * @returns {Promise<object>} Upload result
 */
export const uploadImage = async (file, path, options = {}) => {
  try {
    const url = `https://storage.bunnycdn.com/${BUNNY_STORAGE_ZONE}/${path}`;
    
    const response = await fetch(url, {
      method: 'PUT',
      headers: {
        'AccessKey': BUNNY_API_KEY,
        'Content-Type': file.type,
        'Cache-Control': 'public, max-age=31536000',
        ...options.headers
      },
      body: file
    });

    if (!response.ok) {
      throw new Error(`Upload failed: ${response.status} ${response.statusText}`);
    }

    // Track usage
    trackStorage(file.size);

    return {
      public_id: path,
      secure_url: getImageUrl(path),
      bytes: file.size,
      format: file.type.split('/')[1]
    };
  } catch (error) {
    console.error('Upload failed:', error);
    throw error;
  }
};

/**
 * Delete image from storage
 * @param {string} path - Storage path
 * @returns {Promise<object>} Delete result
 */
export const deleteImage = async (path) => {
  try {
    const url = `https://storage.bunnycdn.com/${BUNNY_STORAGE_ZONE}/${path}`;
    
    const response = await fetch(url, {
      method: 'DELETE',
      headers: {
        'AccessKey': BUNNY_API_KEY
      }
    });

    if (!response.ok) {
      throw new Error(`Delete failed: ${response.status} ${response.statusText}`);
    }

    return { success: true };
  } catch (error) {
    console.error('Delete failed:', error);
    throw error;
  }
};

/**
 * List images in a folder
 * @param {string} folder - Folder path
 * @returns {Promise<Array>} List of images
 */
export const listImages = async (folder = '') => {
  try {
    const url = `https://storage.bunnycdn.com/${BUNNY_STORAGE_ZONE}/${folder}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'AccessKey': BUNNY_API_KEY
      }
    });

    if (!response.ok) {
      throw new Error(`List failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    return data.map(item => ({
      name: item.ObjectName,
      fullPath: item.ObjectName,
      size: item.Length,
      lastChanged: item.LastChanged
    }));
  } catch (error) {
    console.error('List failed:', error);
    throw error;
  }
};

/**
 * Preload image for better performance
 * @param {string} url - Image URL
 * @returns {Promise<string>} Preloaded URL
 */
export const preloadImage = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      trackBandwidth(img.naturalWidth * img.naturalHeight * 4); // Approximate size
      resolve(url);
    };
    img.onerror = reject;
    img.src = url;
  });
};

/**
 * Batch preload images
 * @param {Array<string>} urls - Array of image URLs
 * @param {number} maxConcurrent - Maximum concurrent preloads
 * @returns {Promise<Array<string>>} Preloaded URLs
 */
export const preloadImages = async (urls, maxConcurrent = 3) => {
  const results = [];
  
  for (let i = 0; i < urls.length; i += maxConcurrent) {
    const batch = urls.slice(i, i + maxConcurrent);
    const batchPromises = batch.map(url => 
      preloadImage(url).catch(err => {
        console.warn('Failed to preload image:', url);
        return null;
      })
    );
    
    const batchResults = await Promise.all(batchPromises);
    results.push(...batchResults.filter(Boolean));
  }
  
  return results;
};

/**
 * Create signed URL for private access
 * @param {string} path - Storage path
 * @param {number} expiresIn - Expiration time in seconds
 * @returns {Promise<string>} Signed URL
 */
export const createSignedUrl = async (path, expiresIn = 3600) => {
  // Bunny.net doesn't support signed URLs in the same way
  // For public files, just return the regular URL
  return getImageUrl(path);
};

/**
 * Get CDN URL with optimization
 * @param {string} path - Storage path
 * @param {object} options - CDN options
 * @returns {string} CDN URL
 */
export const getCdnUrl = (path, options = {}) => {
  const baseUrl = getImageUrl(path);
  
  // Add CDN optimization parameters
  const params = new URLSearchParams();
  
  if (options.width) params.append('width', options.width);
  if (options.height) params.append('height', options.height);
  if (options.quality) params.append('quality', options.quality);
  if (options.format) params.append('format', options.format);
  
  return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
};

export default {
  uploadImage,
  getImageUrl,
  getOptimizedImageUrl,
  getThumbnailUrl,
  getFullSizeUrl,
  getRawImageUrl,
  getSmartImageUrl,
  getCdnUrl,
  deleteImage,
  listImages,
  preloadImage,
  preloadImages,
  createSignedUrl,
  getUsageStats,
  resetUsageCounter,
  trackBandwidth,
  trackStorage
}; 